import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { enqueueSnackbar } from 'notistack';
import { QUERY_KEYS } from '../../../../../constants';
import { postData } from '../../../../../utils/api';
import useLocalization from '../../../../../hooks/useLocalization';
import { useModal } from '../../../../../hooks/useDialog';
import NeobetAuthFormShell from '../../auth/NeobetAuthFormShell';
import { NeobetTextField } from '../../molecules/NeobetFormFields';

const getForgotPasswordSchema = (t: ReturnType<typeof useLocalization>['t']) =>
  z.object({
    email: z.email(t('register.validation.invalidEmail')),
  });

type ForgotPasswordData = z.infer<ReturnType<typeof getForgotPasswordSchema>>;

const NeobetForgotPasswordForm = () => {
  const { t } = useLocalization();
  const { closeModal } = useModal();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ForgotPasswordData>({
    resolver: zodResolver(getForgotPasswordSchema(t)),
    defaultValues: {
      email: '',
    },
  });

  const handleClose = useCallback(() => {
    reset();
    closeModal();
  }, [closeModal, reset]);

  const { mutate: forgotPassword } = useMutation({
    mutationKey: [QUERY_KEYS.forgotPassword],
    mutationFn: (data: ForgotPasswordData) => postData('customer/forgot-password', { email: data.email }, 'crm'),
    onSuccess: () => {
      enqueueSnackbar(t('forgotPassword.emailSent'), { variant: 'success' });
      handleClose();
    },
    onError: () => {
      enqueueSnackbar(t('unexpectedError'), { variant: 'error' });
    },
  });

  return (
    <div data-testid='neobet-forgot-password-form'>
      <NeobetAuthFormShell
        title={t('forgotPassword.title')}
        submitTitle={t('forgotPassword.sendResetLink')}
        onSubmit={handleSubmit((data) => forgotPassword(data))}
        onClose={handleClose}
        isSubmitting={isSubmitting}
        testId='neobet-forgot-password-form'
      >
        <NeobetTextField
          name='email'
          control={control}
          label={t('register.email')}
          error={!!errors.email}
          helperText={errors.email?.message}
          disabled={isSubmitting}
        />
      </NeobetAuthFormShell>
    </div>
  );
};

export default NeobetForgotPasswordForm;
